/* eslint-disable @typescript-eslint/no-explicit-any */
/**
 * @copyright (C) 2023, rsupport. All rights reserved
 *
 * @license
 * This software and/or source code may be used, copied and/or disseminated only
 * with the written permission of rsupport, or in accordance with the terms
 * and conditions stipulated in the agreement/contract under which the software
 * and/or source code has been supplied by rsupport or its affiliates.
 * Unauthorized use, copying, or dissemination of this file, via any medium, is
 * strictly prohibited, and will constitute an infringement of copyright.
 *
 * @author
 * Jinhyoung Jang (jangjh@rsupport.com)
 *
 * @description
 *
 * @created at Thu Mar 02 2023
 **/

import { useEffect } from "react";
import isArray from "lodash-es/isArray";
import isObject from "lodash-es/isObject";
import mergeWith from "lodash-es/mergeWith";
import { atom, useAtom } from "jotai";
import { atomFamily } from "jotai/utils";
import { atomWithImmer } from "jotai-immer";
import Properties from "./properties";
import { JotaiStore } from "../commons";
import { Configure } from "../utilities";
import DefaultProperties from "./defaultproperties";

const GlobalProperties = atomWithImmer<AllProperties>(DefaultProperties);
const UpdateProperties = atomFamily((key: string) =>
	atom(
		(get) => get(GlobalProperties)[key],
		(get, set, update: any) => {
			const prev = get(GlobalProperties);
			if (isArray(update)) {
				set(GlobalProperties, {
					...prev,
					[key]: [...prev[key], ...update]
				});
			} else if (isObject(update)) {
				set(GlobalProperties, {
					...prev,
					[key]: {
						...prev[key],
						...update
					}
				});
			} else {
				set(GlobalProperties, {
					...prev,
					[key]: update
				});
			}
			Properties.property = { [key]: update };
		}
	)
);

let initialized = false;
export default function useProperties(initialize: boolean = false) {
	const [globalProperties, setGlobalProperties] = useAtom(GlobalProperties, { store: JotaiStore });

	useEffect(() => {
		if (initialize && !initialized) {
			Properties.property = mergeWith(DefaultProperties, Properties.property, (objValue, srcValue) => {
				if (Array.isArray(objValue)) {
					if (srcValue === null) {
						objValue = null;
						return objValue;
					}
					return objValue.concat(srcValue);
				}
			});
			initialized = true;
		}
	}, []);

	useEffect(() => {
		Configure.useLogLevel === 7 && console.debug("#useProperties# Global properties updated", globalProperties);
	}, [globalProperties]);

	return {
		get default() {
			return DefaultProperties;
		},
		get property() {
			return globalProperties;
		},
		set property(value: Partial<AllProperties>) {
			setGlobalProperties((draft) => {
				Object.entries(value).forEach(([key, data]) => {
					draft[key] = data;
					Properties.property = { [key]: data };
				});
			});
		},
		propertyAtom(key: string) {
			return UpdateProperties(key);
		}
	};
}
